import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { getInitialLeaderboardsSnapshot, getInitialMarketsSnapshot, getLeaderboards, initSession, listMarkets } from "../lib/api";
import { subscribeRealtime } from "../lib/realtime";
import { applyLiveMarketSnapshotsToWindowGroups } from "../lib/liveMarketSnapshots";
import { MarketCard } from "../components/MarketCard";
import { CompactMarketCard } from "../components/CompactMarketCard";
import { HomeHero } from "../components/HomeHero";
import { Seo } from "../components/Seo";
import { CompactMarketCardSkeleton, LeaderboardPanelSkeleton, MarketCardSkeleton } from "../components/skeletons";
import { classifyMarketsByWindow } from "../lib/marketTime";
import { formatCommunityCollectedSol } from "../lib/solDisplay";
import { useCurrentUtcTime } from "../lib/timeDisplay";
import { formatWalletAddress } from "../lib/walletAddress";
import { buildSiteSeo } from "../../shared/seo";

export function HomePage() {
  const [initialMarketsSnapshot] = useState(() => getInitialMarketsSnapshot());
  const [initialLeaderboardsSnapshot] = useState(() => getInitialLeaderboardsSnapshot());
  const hasInitialLeaderboards = hasLeaderboardData(initialLeaderboardsSnapshot);
  const initialGroupedMarkets = useMemo(() => classifyMarketsByWindow(initialMarketsSnapshot), [initialMarketsSnapshot]);
  const [markets, setMarkets] = useState(() => initialGroupedMarkets.active);
  const [upcomingMarkets, setUpcomingMarkets] = useState(() => initialGroupedMarkets.upcoming);
  const marketGroupsRef = useRef(initialGroupedMarkets);
  const [leaderboards, setLeaderboards] = useState(() => initialLeaderboardsSnapshot);
  const [marketsLoaded, setMarketsLoaded] = useState(() => initialMarketsSnapshot.length > 0);
  const [marketStatus, setMarketStatus] = useState(() => (initialMarketsSnapshot.length > 0 ? "success" : "pending"));
  const [leaderboardsLoaded, setLeaderboardsLoaded] = useState(() => hasInitialLeaderboards);
  const [leaderboardStatus, setLeaderboardStatus] = useState(() => (hasInitialLeaderboards ? "success" : "pending"));

  const featuredMarket = useMemo(() => markets[0] || null, [markets]);
  const otherMarkets = useMemo(() => markets.slice(1), [markets]);
  const currentUtc = useCurrentUtcTime();
  const homeHero = (
    <HomeHero
      featuredMarket={featuredMarket}
      activeMarkets={markets}
      upcomingMarkets={upcomingMarkets}
      leaderboards={leaderboards}
      currentUtc={currentUtc}
      marketsLoaded={marketsLoaded}
    />
  );

  const refreshMarkets = useCallback(async () => {
    setMarketStatus("pending");
    try {
      const data = await listMarkets();
      const grouped = classifyMarketsByWindow(data);
      marketGroupsRef.current = grouped;
      setMarkets(grouped.active);
      setUpcomingMarkets(grouped.upcoming);
      setMarketStatus("success");
    } catch (error) {
      setMarketStatus("error");
      throw error;
    } finally {
      setMarketsLoaded(true);
    }
  }, []);

  const refreshLeaderboards = useCallback(async () => {
    setLeaderboardStatus("pending");
    try {
      const data = await getLeaderboards();
      setLeaderboards(data);
      setLeaderboardStatus("success");
    } catch (error) {
      setLeaderboardStatus("error");
      throw error;
    } finally {
      setLeaderboardsLoaded(true);
    }
  }, []);

  const refreshAll = useCallback(async () => {
    await Promise.all([refreshMarkets(), refreshLeaderboards()]);
  }, [refreshLeaderboards, refreshMarkets]);

  const handleLiveMarketUpdate = useCallback(
    (payload) => {
      const grouped = applyLiveMarketSnapshotsToWindowGroups({
        ...marketGroupsRef.current,
        includeClosed: false,
        snapshots: payload
      });
      if (!grouped.changed) {
        refreshMarkets().catch(() => null);
        return;
      }
      marketGroupsRef.current = grouped;
      setMarkets(grouped.active);
      setUpcomingMarkets(grouped.upcoming);
      setMarketStatus("success");
      setMarketsLoaded(true);
    },
    [refreshMarkets]
  );

  useEffect(() => {
    initSession().catch(() => null);
    refreshAll().catch(() => null);
  }, [refreshAll]);

  useEffect(() => {
    return subscribeRealtime({
      topics: ["home", "leaderboards"],
      onMarketUpdate: handleLiveMarketUpdate,
      onLeaderboardsUpdate: refreshLeaderboards
    });
  }, [handleLiveMarketUpdate, refreshLeaderboards]);

  if (!marketsLoaded || (marketStatus === "pending" && !featuredMarket)) {
    return (
      <>
        <Seo metadata={buildSiteSeo({ path: "/" })} />
        <section className="space-y-4" data-route="home">
          {homeHero}
          <div className="grid min-w-0 gap-4 lg:grid-cols-[minmax(0,2.25fr)_minmax(0,1fr)]">
            <div id="active-market" className="home-mobile-rail h-full min-w-0 lg:w-full lg:max-w-none">
              <MarketCardSkeleton className="h-full" />
            </div>
            <LeaderboardPanelSkeleton className="home-mobile-rail h-full lg:w-full lg:max-w-none" />
          </div>
          <article className="route-shell home-mobile-rail space-y-3">
            <h2 className="text-lg font-semibold text-blue-400">Other Active Markets</h2>
            <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
              <CompactMarketCardSkeleton />
              <CompactMarketCardSkeleton />
              <CompactMarketCardSkeleton />
            </div>
          </article>
        </section>
      </>
    );
  }

  if (marketStatus === "error" && !featuredMarket) {
    return (
      <>
        <Seo metadata={buildSiteSeo({ path: "/" })} />
        <section className="space-y-4" data-route="home">
          {homeHero}
          <div className="route-shell home-mobile-rail space-y-3">
            <h2 className="text-lg font-semibold text-zinc-100">Markets are unavailable right now.</h2>
            <p className="max-w-2xl text-sm leading-6 text-zinc-400">
              We could not load the active board. Try again in a moment.
            </p>
            <button type="button" className="theme-button-primary" onClick={() => refreshAll().catch(() => null)}>
              Retry
            </button>
          </div>
        </section>
      </>
    );
  }

  if (!featuredMarket) {
    return (
      <>
        <Seo metadata={buildSiteSeo({ path: "/" })} />
        <section className="space-y-4" data-route="home">
          {homeHero}
          <div className="route-shell home-mobile-rail space-y-2">
            <h2 className="text-lg font-semibold text-zinc-100">No active markets are open right now.</h2>
            <p className="max-w-2xl text-sm leading-6 text-zinc-400">
              The board comes alive when a market enters its voting window. Check upcoming markets or create the next one.
            </p>
            <div className="flex flex-wrap gap-2 pt-1">
              <Link to="/markets" className="theme-button-secondary">
                Check markets
              </Link>
              <Link to="/create" className="theme-button-primary">
                Create a market
              </Link>
            </div>
          </div>
        </section>
      </>
    );
  }

  const featuredLeaderboard = leaderboards.market?.[featuredMarket.id] || [];
  const leaderboardsHaveData = hasLeaderboardData(leaderboards);
  const shouldShowLeaderboardPanel = leaderboardStatus === "success" || leaderboardsHaveData;

  return (
    <>
      <Seo metadata={buildSiteSeo({ path: "/" })} />
      <section className="space-y-4" data-route="home">
        {homeHero}

        <div className="grid min-w-0 gap-4 lg:grid-cols-[minmax(0,2.25fr)_minmax(0,1fr)]">
          <div id="active-market" className="home-mobile-rail h-full min-w-0 lg:w-full lg:max-w-none">
            <MarketCard market={featuredMarket} className="h-full" imagePriority />
          </div>

          {leaderboardsLoaded && shouldShowLeaderboardPanel ? (
            <LeaderboardPanel
              title="Live standings"
              description="See what is moving, who is leading, and which communities are collecting support fees from finalized votes."
              sections={[
                {
                  title: "Featured market leaderboard",
                  content: <LeaderboardList rows={featuredLeaderboard} empty="No votes yet. The first wallet to vote sets the pace." limit={3} />
                },
                {
                  title: "All markets leaderboard",
                  content: (
                    <LeaderboardList
                      rows={leaderboards.global || []}
                      empty="No votes across the board yet. Momentum starts with the first cast vote."
                      limit={3}
                    />
                  )
                },
                {
                  title: "Community fees collected today",
                  content: (
                    <CommunitySupportList
                      rows={leaderboards.community?.day || []}
                      empty="No community fees have been collected today."
                      limit={3}
                    />
                  )
                },
                {
                  title: "Community fees collected this week",
                  content: (
                    <CommunitySupportList
                      rows={leaderboards.community?.week || []}
                      empty="No community fees have been collected this week."
                      limit={3}
                    />
                  )
                },
                {
                  title: "Community fees collected all-time",
                  content: (
                    <CommunitySupportList
                      rows={leaderboards.community?.allTime || []}
                      empty="No community fees have been collected yet."
                      limit={3}
                    />
                  )
                },
                {
                  title: "Affiliate all-time",
                  content: (
                    <AffiliateLeaderboardList
                      rows={leaderboards.affiliate?.allTime || []}
                      empty="No referral momentum yet. Shared links will show here after referred votes land."
                      limit={3}
                    />
                  )
                },
                {
                  title: "Affiliate UTC today",
                  content: (
                    <AffiliateLeaderboardList
                      rows={leaderboards.affiliate?.day || []}
                      empty="No referred votes today. This resets on the UTC day."
                      limit={3}
                    />
                  )
                }
              ]}
            />
          ) : leaderboardsLoaded && leaderboardStatus === "error" ? (
            <LeaderboardUnavailablePanel />
          ) : (
            <LeaderboardPanelSkeleton className="home-mobile-rail h-full lg:w-full lg:max-w-none" />
          )}
        </div>

        <article className="route-shell home-mobile-rail space-y-3">
          <h2 className="text-lg font-semibold text-blue-400">Other Active Markets</h2>
          {otherMarkets.length === 0 ? (
            <p className="text-sm leading-6 text-zinc-400">
              This is the full active board right now. New markets will appear here as their voting windows open.
            </p>
          ) : (
            <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
              {otherMarkets.map((market) => (
                <CompactMarketCard key={market.id} market={market} />
              ))}
            </div>
          )}
        </article>
      </section>
    </>
  );
}

function LeaderboardList({ rows, empty, limit = 8 }) {
  if (!rows || rows.length === 0) {
    return <p className="text-sm text-zinc-400">{empty}</p>;
  }

  return (
    <ol className="space-y-1 text-sm">
      {rows.slice(0, limit).map((row, index) => (
        <li key={`${row.userId}-${index}`} className="flex min-w-0 items-center justify-between gap-2 border-b border-zinc-800 pb-1">
          <span className="min-w-0 flex-1 truncate text-zinc-200">
            {index + 1}. {row.displayName || formatWalletAddress(row.userId)}
          </span>
          <span className="shrink-0 text-zinc-300">{formatVoteAmount(row.votes)}</span>
        </li>
      ))}
    </ol>
  );
}

function AffiliateLeaderboardList({ rows, empty, limit = 8 }) {
  if (!rows || rows.length === 0) {
    return <p className="text-sm text-zinc-400">{empty}</p>;
  }

  return (
    <ol className="space-y-1 text-sm">
      {rows.slice(0, limit).map((row, index) => (
        <li
          key={`${row.affiliateUserId}-${index}`}
          className="flex min-w-0 items-center justify-between gap-2 border-b border-zinc-800 pb-1"
        >
          <span className="min-w-0 flex-1 truncate text-zinc-200">
            {index + 1}. {row.displayName || formatWalletAddress(row.affiliateUserId)}
          </span>
          <span className="shrink-0 text-zinc-300">{formatVoteAmount(row.referredVotes)}</span>
        </li>
      ))}
    </ol>
  );
}

function CommunitySupportList({ rows, empty, limit = 8 }) {
  if (!rows || rows.length === 0) {
    return <p className="text-sm text-zinc-400">{empty}</p>;
  }

  return (
    <ol className="space-y-1 text-sm">
      {rows.slice(0, limit).map((row, index) => (
        <li key={`${row.slug || row.name}-${index}`} className="border-b border-zinc-800 pb-1">
          <div className="flex min-w-0 items-center justify-between gap-2">
            <span className="min-w-0 flex-1 truncate text-zinc-200">
              {index + 1}. {row.name || "Kreechures"}
            </span>
            <span className="shrink-0 text-zinc-300">{formatCommunityCollectedSol(row.supportedSol)}</span>
          </div>
          <p className="mt-0.5 text-xs text-zinc-500">
            {formatInteger(row.voteCount)} votes · {formatInteger(row.marketCount)} markets
          </p>
        </li>
      ))}
    </ol>
  );
}

function formatVoteAmount(value) {
  return Number(value || 0).toFixed(2);
}

function formatInteger(value) {
  return Number(value || 0).toLocaleString("en-US", { maximumFractionDigits: 0 });
}

function hasLeaderboardData(snapshot) {
  return Boolean(
    snapshot?.global?.length ||
      Object.keys(snapshot?.market || {}).length ||
      snapshot?.affiliate?.allTime?.length ||
      snapshot?.affiliate?.day?.length ||
      snapshot?.community?.day?.length ||
      snapshot?.community?.week?.length ||
      snapshot?.community?.allTime?.length
  );
}

function LeaderboardUnavailablePanel() {
  return (
    <aside
      className="route-shell home-mobile-rail h-full min-h-[32rem] min-w-0 space-y-3 lg:min-h-[39rem] lg:w-full lg:max-w-none"
      data-cls-measure="leaderboard-panel"
    >
      <p className="text-xs font-semibold uppercase tracking-[0.22em] text-zinc-500">Leaderboard snapshot</p>
      <h3 className="text-lg font-semibold text-zinc-100">Standings are unavailable.</h3>
      <p className="text-sm leading-6 text-zinc-400">We could not load leaderboard data. Try again in a moment.</p>
    </aside>
  );
}

function LeaderboardPanel({ title, description, sections }) {
  return (
    <aside
      className="route-shell home-mobile-rail h-full min-w-0 space-y-4 lg:w-full lg:max-w-none"
      data-cls-measure="leaderboard-panel"
    >
      <div className="space-y-1">
        <p className="text-xs font-semibold uppercase tracking-[0.22em] text-zinc-500">Leaderboard snapshot</p>
        <h3 className="text-lg font-semibold text-zinc-100">{title}</h3>
        <p className="text-sm leading-6 text-zinc-400">{description}</p>
      </div>

      <div className="space-y-3">
        {sections.map((section, index) => (
          <section
            key={section.title}
            className={index === 0 ? "space-y-2" : "space-y-2 border-t border-zinc-800 pt-3"}
          >
            <h4 className="text-sm font-semibold uppercase tracking-wide text-blue-400">{section.title}</h4>
            {section.content}
          </section>
        ))}
      </div>
    </aside>
  );
}
